import type { AiModelRecord, ModelListFilters } from "./types.js";

function matchesSearch(record: AiModelRecord, search: string): boolean {
  const q = search.toLowerCase();
  return (
    record.modelId.toLowerCase().includes(q) ||
    record.name.toLowerCase().includes(q) ||
    record.description.toLowerCase().includes(q) ||
    record.canonicalSlug.toLowerCase().includes(q) ||
    record.aliases.some((a) => a.toLowerCase().includes(q))
  );
}

/**
 * In-memory filter over already-loaded {@link AiModelRecord} rows.
 *
 * Prefer {@link listAICatalogModels} / {@link AiModelsService.listModels} for
 * catalog attribute filters (`provider`, `status`, modalities, tools, reasoning).
 * This helper remains for `supportedParameter`, pagination, and local maps.
 */
export function filterModels(
  models: Iterable<AiModelRecord>,
  filters: ModelListFilters = {},
): AiModelRecord[] {
  let list = [...models];

  if (filters.providerId) {
    list = list.filter((m) => m.providerId === filters.providerId);
  }
  if (filters.status) {
    list = list.filter((m) => m.status === filters.status);
  }
  if (filters.outputModality) {
    list = list.filter((m) => m.outputModalities.includes(filters.outputModality!));
  }
  if (filters.inputModality) {
    list = list.filter((m) => m.inputModalities.includes(filters.inputModality!));
  }
  if (filters.supportedParameter) {
    list = list.filter((m) => m.supportedParameters.includes(filters.supportedParameter!));
  }
  if (filters.supportsTools !== undefined) {
    list = list.filter((m) => m.supportsTools === filters.supportsTools);
  }
  if (filters.supportsReasoning !== undefined) {
    list = list.filter((m) => m.supportsReasoning === filters.supportsReasoning);
  }
  if (filters.search) {
    list = list.filter((m) => matchesSearch(m, filters.search!));
  }

  list.sort((a, b) => a.name.localeCompare(b.name));

  const offset = filters.offset ?? 0;
  const limit = filters.limit ?? list.length;
  return list.slice(offset, offset + limit);
}

export function countModels(
  models: Iterable<AiModelRecord>,
  filters: Omit<ModelListFilters, "limit" | "offset"> = {},
): number {
  return filterModels(models, { ...filters, limit: Number.MAX_SAFE_INTEGER, offset: 0 }).length;
}
